import { useLayoutEffect, useState, type RefObject } from 'react';

/** Keep layout height stable; move the sticky inset, never transform the
 * header (its drawers must remain fixed to the viewport). */
export function useScrollAwayHeader(ref: RefObject<HTMLElement>, enabled: boolean) {
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    let hidden = false;
    let height = el.offsetHeight;
    let previousY = window.scrollY;
    let travel = 0;
    let jumping = false;

    const publish = () => {
      setTop(hidden ? -height : 0);
      root.style.setProperty('--site-header-h', `${hidden ? 0 : height}px`);
    };
    const reveal = () => {
      hidden = false;
      travel = 0;
      publish();
    };
    const measure = () => {
      height = el.offsetHeight;
      publish();
    };
    const onScroll = () => {
      // Clamp elastic overscroll so bouncing at either end cannot reverse us.
      const maxY = Math.max(0, root.scrollHeight - window.innerHeight);
      const y = Math.max(0, Math.min(window.scrollY, maxY));
      const delta = y - previousY;
      previousY = y;
      if (y <= 8) { reveal(); return; }
      if (jumping || document.body.style.overflow === 'hidden' ||
          (el.contains(document.activeElement) && document.activeElement?.matches(':focus-visible'))) {
        travel = 0;
        return;
      }
      // At the very end, bring the header (and its cart) back rather than
      // leaving the floating cart over the footer's last controls.
      if (y >= maxY - 8) { reveal(); return; }
      if (delta === 0) return;
      travel = Math.sign(delta) === Math.sign(travel) ? travel + delta : delta;
      if (Math.abs(travel) < 10) return;
      hidden = travel > 0 && y > height;
      travel = 0;
      publish();
    };
    // Category jumps have their own position correction. Freeze the current
    // inset until the next gesture, or the two scroll controllers fight.
    const onJump = () => { jumping = true; travel = 0; };
    const onGesture = () => {
      if (!jumping) return;
      jumping = false;
      travel = 0;
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    if (enabled) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('menu-section-scroll', onJump);
      window.addEventListener('wheel', onGesture, { passive: true });
      window.addEventListener('touchstart', onGesture, { passive: true });
      window.addEventListener('keydown', onGesture);
      el.addEventListener('focusin', reveal);
    }
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('menu-section-scroll', onJump);
      window.removeEventListener('wheel', onGesture);
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('keydown', onGesture);
      el.removeEventListener('focusin', reveal);
      root.style.removeProperty('--site-header-h');
    };
  }, [ref, enabled]);

  return top;
}
